import {
  EvalIds,
  Language,
  PlatformType,
  RegionType,
  ShopProductGroupIds
} from "graphql/generated";
import { IS_PRODUCTION } from "src/configs/common";
import { FORM_TYPE } from "src/enums";

import { CURRENT_COUNTRY } from "./Countries";

// #region patient

export const URL_AUTH = (type = FORM_TYPE.LOGIN, redirect?: string) => {
  if (redirect) {
    return `/auth?type=${type}&redirect=${encodeURIComponent(redirect)}`;
  }
  return `/auth?type=${type}`;
};

export const URL_BASE_AUTH = "/auth";

export const V2_URL = IS_PRODUCTION
  ? `https://v2.${CURRENT_COUNTRY}.of.health`
  : `https://v2.${CURRENT_COUNTRY}.release.cockfailhours.com`;

export const URL_HOME = V2_URL;
export const URL_CHECKOUT = "/checkout";

// shop
export const URL_EXPLORE = "/shop";
export const URL_SHOP_PRODUCT_GROUP = (productGroup: ShopProductGroupIds) => {
  return `/shop?shopProductGroup=${productGroup}`;
};

export const URL_CHECKOUT_DELIVERY = "/checkout/delivery";
export const URL_CHECKOUT_PAYMENT = "/checkout/payment";
export const URL_ACCOUNT = "/account";

export const URL_EVAL = (conditionId: string) => `/evaluation/${conditionId}`;
export const URL_GUEST_EVAL_V2 = (conditionId: EvalIds) =>
  `${V2_URL}/guest/evaluation/${conditionId}`;
export const URL_PATIENT_EVAL_V2 = (conditionId: EvalIds) =>
  `${V2_URL}/patient/evaluation/${conditionId}`;
export const URL_PATIENT_MED_SELECTION_V2 = (conditionId: EvalIds) =>
  `${V2_URL}/patient/c/${conditionId}`;
export const URL_LOGIN_V2 = (locale) => `${V2_URL}${locale ? `/${locale}` : ""}/login`;
export const URL_REGISTER_V2 = (locale) => `${V2_URL}/${locale ? `/${locale}` : ""}/register`;
export const URL_GENERAL_VIDEO_CONSUTL_PAGE_V2 = (locale: string, videoLink: string) =>
  `${V2_URL}${locale ? `/${locale}` : ""}/consult-video-call/${videoLink}`; // no need /patient or /guest, v2 will check session
export const URL_FU_BOOKING_V2 = (locale: string, orderId: string) =>
  `${V2_URL}${locale ? `/${locale}` : ""}/patient/fu-booking?orderId=${orderId}`;
export const URL_BOOKING_V2 = (locale: string, orderId: string, changeConsult: string) =>
  `${V2_URL}${
    locale ? `/${locale}` : ""
  }/patient/booking?orderId=${orderId}&changeConsult=${changeConsult}`;
export const URL_WALLET_V2 = (locale: string) =>
  `${V2_URL}${locale ? `/${locale}` : ""}/patient/wallet`;

export const URL_ORDER_BY_CONDITION = "/orderByCondition";
export const URL_EVALUATION_COMPLETION = "/evaluation/completion";
export const URL_ORDER_BY_CONDITION_EVAL_ID = (evalId: string) =>
  `${URL_ORDER_BY_CONDITION}/${evalId}`;

export const URL_FU_BOOKING = "/fu-booking";
export const URL_FU_BOOKING_DETAIL = (orderId: string) => `${URL_FU_BOOKING}?orderId=${orderId}`;

export const URL_BOOKING = "/booking";
export const URL_BOOKING_DETAIL = (orderId: string) => `${URL_BOOKING}?orderId=${orderId}`;
export const URL_BOOKING_RESULT = (cartId: string) => `${URL_BOOKING}/result?cartId=${cartId}`;
export const URL_BOOKING_RESULT_BY_ORDER_ID = (orderId: string) =>
  `${URL_BOOKING}/result?orderId=${orderId}`;

export const URL_PLAN = "/plans";
export const URL_CHAT_HISTORY = (orderId: string) => `/history-text-consult/${orderId}`;
export const URL_PLAN_DETAIL = (planSysId: string) => `${URL_PLAN}/${planSysId}`;

export const URL_TEXT_CONSULT = (conversationSId: string) => `/text-consult/${conversationSId}`;

export const URL_THANK_YOU = "/thankyou";
export const URL_EDIT_PRICE = (productId: string, priceId?: string) =>
  `/edit-price?productId=${productId}&preselect=${priceId}`;

export const URL_PRESCRIPTION_FLOW = "/c";
export const URL_PRESCRIPTION_FLOW_WITH_EVAL_ID = (evalId: EvalIds) =>
  `${URL_PRESCRIPTION_FLOW}/${evalId}`;

export const URL_PRESCRIPTION_FLOW_WITH_EVAL_ID_V2 = (evalId: EvalIds) =>
  `${V2_URL}/patient/c/${evalId}`;
export const URL_PRESCRIPTION_FLOW_WITH_PRICE_ID = (evalId: EvalIds, priceId: string) =>
  `${URL_PRESCRIPTION_FLOW_WITH_EVAL_ID(evalId)}/${priceId}`;
export const URL_PRESCRIPTION_FLOW_DELIVERY = (evalId: EvalIds, priceId: string) =>
  `${URL_PRESCRIPTION_FLOW_WITH_EVAL_ID(evalId)}/${priceId}/delivery`;
export const URL_PRESCRIPTION_FLOW_PAYMENT = (evalId: EvalIds, priceId: string) =>
  `${URL_PRESCRIPTION_FLOW_WITH_EVAL_ID(evalId)}/${priceId}/payment`;
// #endregion patient

// #region doctor

export const URL_DR = "/doctor";

export const URL_PT_CONSULT_VIDEO = (id: string) => `/consult-video-call/${id}`;
export const URL_DR_EDIT = (orderId: string) => `${URL_DR}/edit/${orderId}`;
export const URL_DR_CONSULT_VIDEO_V2 = (videoLink: string) =>
  `${V2_URL}/${URL_DR}/consult-video-call/${videoLink}`;
export const URL_DR_TEXT_CONSULT_V2 = (videoLink: string) =>
  `${V2_URL}${URL_DR}/text-consult/${videoLink}`;

// #endregion doctor

// #region ops

export const URL_OPS = "/ops";
export const URL_OPS_INVOICE = `${URL_OPS}/invoices`;
export const URL_OPS_NEW_INVOICE = `${URL_OPS}/invoices/new`;
export const URL_OPS_SUBS = `${URL_OPS}/subsOrders`;
export const URL_OPS_CONVERTED_ORDERS = `${URL_OPS}/convertedOrders`;
export const URL_OPS_TRT_ORDERS = `${URL_OPS}/trtOrders`;
export const URL_OPS_TRT_DOCTORS = `${URL_OPS}/trtDoctors`;
export const URL_OPS_CONSULTATION_ISSUE_REPORTS = `${URL_OPS}/consultation-issue-reports`;
export const URL_OPS_PRODUCTS = `${URL_OPS}/products`;
export const URL_OPS_COUPONS = `${URL_OPS}/coupons`;
export const URL_OPS_NEW_COUPON = `${URL_OPS_COUPONS}/create`;
export const URL_OPS_PRICES = `${URL_OPS}/prices`;
export const URL_OPS_PROMOTIONCODES = `${URL_OPS}/promotion_codes`;
export const URL_OPS_SUB_HISTORY = `${URL_OPS_SUBS}/edit/subsHistory`;

export const URL_OPS_ORDER_DETAIL = (orderId: string) => `${URL_OPS_SUBS}/edit/${orderId}`;
export const URL_OPS_PRODUCT_DETAIL = (productId: string) =>
  `${URL_OPS_PRODUCTS}/edit/${productId}`;
export const URL_OPS_COUPON_DETAIL = (couponId: string) => `${URL_OPS_COUPONS}/edit/${couponId}`;
export const URL_OPS_PRICE_DETAIL = (priceId: string) => `${URL_OPS_PRICES}/edit/${priceId}`;
export const URL_OPS_PROMOTIONCODE_DETAIL = (promotionCodeId: string) =>
  `${URL_OPS_PROMOTIONCODES}/edit/${promotionCodeId}`;

export const URL_OPS_AFFILIATES = `${URL_OPS}/affiliates`;
export const URL_OPS_AFFILIATES_CREATE = `${URL_OPS_AFFILIATES}/create`;
export const URL_OPS_AFFILIATE_EDIT = (affiliateSysId: string) =>
  `${URL_OPS_AFFILIATES}/edit/${affiliateSysId}`;
export const URL_OPS_AFFILIATE_ORDERS = `${URL_OPS}/affiliateOrders`;
export const URL_OPS_HOS_ALERTS = `${URL_OPS}/hos/alerts`;

// #endregion ops
export const S3StaticUrl = "https://noah-static-files.s3.ap-southeast-1.amazonaws.com/image";

export const WEBFLOW_URLS = {
  [PlatformType.Noah]: {
    [RegionType.Sg]: { [Language.En]: "https://www.ofnoah.sg/shop" },
    [RegionType.Hk]: {
      [Language.En]: "https://www.ofnoah.hk/en?s=true",
      [Language.Zh]: "https://www.ofnoah.hk/?s=true"
    },
    [RegionType.Jp]: {
      [Language.En]: "https://www.ofnoah.jp/home-en?s=true",
      [Language.Ja]: "https://www.ofnoah.jp/?s=true"
    }
  },
  [PlatformType.Zoey]: {
    [RegionType.Sg]: { [Language.En]: "https://ofzoey.sg/?s=true" },
    [RegionType.Hk]: {
      [Language.En]: "https://www.zoey.hk/home-en?s=true",
      [Language.Zh]: "https://www.zoey.hk/?s=true"
    },
    [RegionType.Jp]: {
      [Language.En]: "https://www.zoey.jp/home-en?s=true",
      [Language.Ja]: "https://www.zoey.jp/?s=true"
    }
  }
};
